import React, { useState, useEffect } from "react";
import { Box, Button, Typography } from "@mui/material";
import FlexBox from "components/common/FlexBox";
import AILoading from "components/common/AILoading";
import { ReactComponent as SparklesIcon } from "../../../icons/Advisor/Sparkles.svg";
import {
  Baby,
  Heart,
  PawPrint,
  Home,
  Building2,
  Brain,
  Plane,
  Wrench,
  PartyPopper,
  HeartPulse,
  DollarSign,
  Shuffle,
  Pencil,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  TaskDiscoveryItem,
  UserProfile,
  filterTasksByProfile,
  getRandomTasks,
} from "./taskDiscoveryData";
import theme from "theme";
import { useGetSignupSessionForMember } from "services/signup";
import { GetSignupSessionForMemberRequest } from "protogen/signup_service_pb";
import useIsMobile from "components/hooks/useIsMobile";

type RoleKey = keyof UserProfile["roles"];
type GoalKey = keyof UserProfile["goals"];

const ROLES: Array<{ key: RoleKey; label: string; icon: any }> = [
  { key: "parent", label: "Parent", icon: Baby },
  { key: "partner", label: "Partner/Spouse", icon: Heart },
  { key: "petOwner", label: "Pet owner", icon: PawPrint },
  { key: "homeowner", label: "Homeowner", icon: Home },
  { key: "renter", label: "Renter", icon: Home },
  { key: "businessOwner", label: "Business owner", icon: Building2 },
];

const GOALS: Array<{ key: GoalKey; label: string; icon: any }> = [
  { key: "mental-load", label: "Reduce mental load", icon: Brain },
  { key: "travel", label: "Organize travel", icon: Plane },
  { key: "fun", label: "Add more fun", icon: PartyPopper },
  { key: "maintenance", label: "Keep up with maintenance", icon: Wrench },
  { key: "save-money", label: "Save money", icon: DollarSign },
  { key: "health", label: "Stay on top of health", icon: HeartPulse },
];

const TaskDiscoverySelector = ({
  onGenerate,
  initialProfile,
}: {
  onGenerate: (profile: UserProfile) => void;
  initialProfile?: UserProfile | null;
}) => {
  const isMobile = useIsMobile();
  const [selectedRoles, setSelectedRoles] = useState<Set<RoleKey>>(() => {
    if (!initialProfile) return new Set();
    const roles = new Set<RoleKey>();
    (Object.keys(initialProfile.roles) as RoleKey[]).forEach((key) => {
      if (initialProfile.roles[key]) roles.add(key);
    });
    return roles;
  });
  const [selectedGoals, setSelectedGoals] = useState<Set<GoalKey>>(() => {
    if (!initialProfile) return new Set();
    const goals = new Set<GoalKey>();
    (Object.keys(initialProfile.goals) as GoalKey[]).forEach((key) => {
      if (initialProfile.goals[key]) goals.add(key);
    });
    return goals;
  });

  // Update state when initialProfile changes
  React.useEffect(() => {
    if (initialProfile) {
      const roles = new Set<RoleKey>();
      (Object.keys(initialProfile.roles) as RoleKey[]).forEach((key) => {
        if (initialProfile.roles[key]) roles.add(key);
      });
      setSelectedRoles(roles);

      const goals = new Set<GoalKey>();
      (Object.keys(initialProfile.goals) as GoalKey[]).forEach((key) => {
        if (initialProfile.goals[key]) goals.add(key);
      });
      setSelectedGoals(goals);
    }
  }, [initialProfile]);

  const toggleRole = (role: RoleKey) => {
    setSelectedRoles((prev) => {
      const next = new Set(prev);
      if (next.has(role)) {
        next.delete(role);
      } else {
        next.add(role);
      }
      return next;
    });
  };

  const toggleGoal = (goal: GoalKey) => {
    setSelectedGoals((prev) => {
      const next = new Set(prev);
      if (next.has(goal)) {
        next.delete(goal);
      } else {
        next.add(goal);
      }
      return next;
    });
  };

  const handleGenerate = () => {
    const profile: UserProfile = {
      roles: {},
      goals: {},
    };

    selectedRoles.forEach((role) => {
      profile.roles[role] = true;
    });

    selectedGoals.forEach((goal) => {
      profile.goals[goal] = true;
    });

    onGenerate(profile);
  };

  const optionStyle = (isSelected: boolean) => ({
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px",
    height: "60px",
    border: isSelected
      ? `1.5px solid ${theme.palette.teal[4]}`
      : "1px solid #D4D4D4",
    borderRadius: "12px",
    cursor: "pointer",
    backgroundColor: isSelected ? "#F0F9F9" : "white",
    transition: "all 0.1s",
    userSelect: "none",
  });

  const stackedOptionStyle = (isSelected: boolean) => ({
    ...optionStyle(isSelected),
    flexDirection: "column",
    alignItems: "center",
    height: "auto",
    gap: "4px",
  });

  return (
    <FlexBox
      direction="column"
      variant={isMobile ? "default" : "contained"}
      gap="16px"
      sx={{ padding: isMobile ? "0" : "24px" }}
    >
      <Typography variant="h4">
        <span
          style={{
            display: "inline-block",
            width: "20px",
            verticalAlign: "top",
            marginRight: "8px",
          }}
        >
          <SparklesIcon width="20px" height="20px" />
        </span>
        Share more to get personalized{isMobile ? " tasks" : " task ideas"}
      </Typography>

      <FlexBox direction="column" gap="12px">
        <Typography variant="bodyHeavy" color="#3D3D3D">
          I am a...
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(3, 1fr)",
            gap: "12px",
          }}
        >
          {ROLES.map((role) => {
            const Icon = role.icon;
            const isSelected = selectedRoles.has(role.key);
            return (
              <Box
                key={role.key}
                onClick={() => toggleRole(role.key)}
                sx={
                  isMobile
                    ? stackedOptionStyle(isSelected)
                    : optionStyle(isSelected)
                }
              >
                <Box sx={{ width: 20, height: 20, flexShrink: 0 }}>
                  <Icon size={20} color={isSelected ? "#198282" : "#8E9598"} />
                </Box>
                <Typography variant={isSelected ? "bodyHeavy" : "body"}>
                  {role.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </FlexBox>

      <FlexBox direction="column" gap="12px">
        <Typography variant="bodyHeavy" color="#3D3D3D">
          My goals are to...
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: isMobile ? "repeat(1, 1fr)" : "repeat(3, 1fr)",
            gap: "12px",
          }}
        >
          {GOALS.map((goal) => {
            const Icon = goal.icon;
            const isSelected = selectedGoals.has(goal.key);
            return (
              <Box
                key={goal.key}
                onClick={() => toggleGoal(goal.key)}
                sx={optionStyle(isSelected)}
              >
                <Box sx={{ width: 20, height: 20, flexShrink: 0 }}>
                  <Icon size={20} color={isSelected ? "#198282" : "#8E9598"} />
                </Box>
                <Typography variant={isSelected ? "bodyHeavy" : "body"}>
                  {goal.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </FlexBox>

      <Button variant="outlined" onClick={handleGenerate} sx={{}}>
        Generate task ideas
      </Button>
    </FlexBox>
  );
};

const TaskItem = ({
  title,
  onAdd,
}: {
  title: string;
  onAdd: (title: string) => void;
}) => {
  return (
    <FlexBox
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        padding: "5px 0",
      }}
    >
      <Typography variant="body">{title}</Typography>
      <Button
        size="small"
        variant="text"
        onClick={() => onAdd(title)}
        sx={{ height: "unset" }}
      >
        Add
      </Button>
    </FlexBox>
  );
};

const TaskDiscoveryResults = ({
  onEditResponses,
  onAdd,
  onShuffle,
  tasks,
}: {
  onEditResponses: () => void;
  onAdd: (title: string) => void;
  onShuffle: () => void;
  tasks: TaskDiscoveryItem[];
}) => {
  const isMobile = useIsMobile();
  return (
    <FlexBox
      direction="column"
      variant="contained"
      gap="12px"
      sx={{ padding: "24px" }}
    >
      <FlexBox gap="8px" alignItems="center">
        <SparklesIcon height="20px" width="20px" />
        <Typography
          variant="h4"
          color="#3D3D3D"
          sx={{
            display: "flex",
            lineHeight: 1,
          }}
        >
          Personalized task ideas
        </Typography>
      </FlexBox>

      <FlexBox direction="column" gap="4px">
        {tasks.map((task, index) => (
          <TaskItem key={index} title={task.name} onAdd={onAdd} />
        ))}
      </FlexBox>

      <FlexBox
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Button
          size="small"
          variant="outlined"
          onClick={onShuffle}
          startIcon={<Shuffle size={16} />}
        >
          {isMobile ? "Shuffle" : "Shuffle ideas"}
        </Button>
        <Button
          onClick={onEditResponses}
          variant="text"
          startIcon={<Pencil size={20} />}
        >
          {isMobile ? "Edit" : "Edit my responses"}
        </Button>
      </FlexBox>
    </FlexBox>
  );
};

export default ({ initialProfile }: { initialProfile?: UserProfile }) => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(
    initialProfile || null,
  );
  const [currentTasks, setCurrentTasks] = useState<TaskDiscoveryItem[]>([]);
  const [isEditing, setIsEditing] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const getSignupSession = useGetSignupSessionForMember((response) => {
    const signupGoals = response.state?.goals;
    if (signupGoals && Object.keys(signupGoals).length > 0) {
      const userProfile: UserProfile = {
        roles: {},
        goals: {},
      };

      Object.entries(signupGoals).forEach(
        ([key, choiceValue]: [string, any]) => {
          if (choiceValue?.value) {
            userProfile.goals[key as GoalKey] = true;
          }
        },
      );

      setProfile(userProfile);
    }
  });

  useEffect(() => {
    if (!initialProfile) {
      getSignupSession.request(new GetSignupSessionForMemberRequest());
    }
  }, [initialProfile]);

  const handleGenerate = (newProfile: UserProfile) => {
    setIsLoading(true);

    setTimeout(() => {
      setProfile(newProfile);
      setIsEditing(false);
      const filtered = filterTasksByProfile(newProfile, 0.3);
      setCurrentTasks(getRandomTasks(filtered, 8, false));
      setIsLoading(false);
    }, 2000);
  };

  const handleShuffle = () => {
    if (profile) {
      setIsLoading(true);

      setTimeout(() => {
        const filtered = filterTasksByProfile(profile, 0.8);
        setCurrentTasks(getRandomTasks(filtered, 8, true));
        setIsLoading(false);
      }, 1000);
    }
  };

  const handleEditResponses = () => {
    setIsEditing(true);
  };

  const handleAdd = (title: string) => {
    navigate("/tasks/add", {
      state: { taskTitle: title },
    });
  };

  const showSelector = !profile || isEditing;

  if (isLoading) {
    return (
      <FlexBox
        direction="column"
        variant="contained"
        gap="12px"
        sx={{ padding: "24px" }}
      >
        <FlexBox gap="8px" alignItems="center">
          <SparklesIcon height="20px" width="20px" />
          <Typography
            variant="h4"
            color="#3D3D3D"
            sx={{
              display: "flex",
              lineHeight: 1,
            }}
          >
            Personalized task ideas
          </Typography>
        </FlexBox>

        <FlexBox
          direction="column"
          sx={{ minHeight: "277px", justifyContent: "center" }}
        >
          <AILoading messages="newUser" />
        </FlexBox>

        <FlexBox
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Button
            size="small"
            variant="outlined"
            onClick={handleShuffle}
            startIcon={<Shuffle size={16} />}
            disabled={isLoading}
          >
            Shuffle ideas
          </Button>
          <Button
            onClick={handleEditResponses}
            variant="text"
            startIcon={<Pencil size={20} />}
            disabled={isLoading}
          >
            Edit my responses
          </Button>
        </FlexBox>
      </FlexBox>
    );
  }

  return (
    <>
      {showSelector ? (
        <TaskDiscoverySelector
          onGenerate={handleGenerate}
          initialProfile={profile || undefined}
        />
      ) : (
        <TaskDiscoveryResults
          onEditResponses={handleEditResponses}
          onAdd={handleAdd}
          onShuffle={handleShuffle}
          tasks={currentTasks}
        />
      )}
    </>
  );
};
